import { ImagePlus, MessageSquarePlus } from 'lucide-react'
import { LoadingState } from '@highlight/ui'
import { useReminderActions, type ReminderItem } from '../../../hooks/use-reminders'
import { CellPreviewCard } from '../../cell-preview-card'
import { useStartReminderChat } from '../hooks/use-start-reminder-chat'
import { ReminderPills } from '../reminder-preview'

interface ReminderDetailActionsProps {
  item: ReminderItem
  /** Names the surface on every event these controls send. */
  trigger: string
  /** After a change the host wants to flash as saved. */
  onSaving?: () => void
  /**
   * Left off where the host already offers the reminder to chat — the popover's
   * description card carries its own prompt, so a Chat pill there would be a
   * second way to the same place.
   */
  showsChat?: boolean
  /**
   * The attach-image route, from the host's `useReminderImageAttach`. Without
   * one a surface can have no way to attach an image at all, which is how the
   * Reminder View lost one.
   */
  attach?: { openPicker: () => void; attaching: boolean }
  /** Run before a control navigates away, for a host that has to close first. */
  onLeave?: () => void
}

/**
 * A reminder's moves, as a row: its due date, its pin, and a way into a chat
 * about it. Shared by the row's popover and the Reminder View, so the two can't
 * offer different things.
 */
export function ReminderDetailActions({
  item,
  trigger,
  onSaving,
  showsChat = false,
  attach,
  onLeave,
}: ReminderDetailActionsProps) {
  const { updateItem } = useReminderActions()
  const startReminderChat = useStartReminderChat()

  return (
    <>
      <ReminderPills
        item={item}
        trigger={trigger}
        onSnooze={(date) => {
          updateItem(item.id, item.title, date, { trigger })
          onSaving?.()
        }}
        onMutated={onSaving}
      />
      {attach && (
        <CellPreviewCard.PillButton
          aria-label="Add image"
          aria-busy={attach.attaching}
          disabled={attach.attaching}
          onClick={attach.openPicker}
        >
          {attach.attaching ? <LoadingState.Spinner className="size-3.5 shrink-0" /> : <ImagePlus />}
          Image
        </CellPreviewCard.PillButton>
      )}
      {showsChat && (
        <CellPreviewCard.PillButton
          className="ml-auto"
          onClick={() => {
            onLeave?.()
            void startReminderChat(item, trigger)
          }}
        >
          <MessageSquarePlus />
          Chat
        </CellPreviewCard.PillButton>
      )}
    </>
  )
}
